import type { SelectionMode } from "../viewer/rect-selection";
import { useI18n } from "../i18n";
import { cn } from "./cn";

/**
 * WO-P5 iter4 §MODE-TOGGLE — two explicit interaction modes for
 * the Grid, surfaced inside the Context panel toolbar:
 *
 *   • "select" (croce / crosshair) — the canonical purchase-flow
 *     mode. Drag on the canvas draws a rectangle selection. The
 *     legacy click-to-inspect behaviour is intentionally kept
 *     (option A per Claudio directive) so this toggle only makes
 *     the two modes EXPLICIT — it does not remove any existing
 *     interaction.
 *
 *   • "browse" (manina / hand) — inspect-only. Rectangle drag on
 *     the canvas is disabled (see `beginDraft` in
 *     viewer/rect-selection.ts: no-op for mode !== "select"), so
 *     the user can freely click parcels to open the Parcel
 *     Profile without accidentally starting a selection.
 *
 * The cursor cue on the canvas already follows this mode via
 * `GridViewer`'s inline style (crosshair vs grab), so this
 * component only owns the toggle chrome and delegates authority
 * back to the shell (`onChange`).
 */

export interface ContextModeToggleProps {
  mode: SelectionMode;
  onChange: (next: SelectionMode) => void;
  /** WO-VRD-30 — single-row compact variant for the top instrument bar. */
  compact?: boolean;
}

export function ContextModeToggle({
  mode,
  onChange,
  compact = false,
}: ContextModeToggleProps) {
  const { t } = useI18n();
  return (
    <div
      data-testid="context-mode-toggle"
      data-active-mode={mode}
      role="radiogroup"
      aria-label={t("grid.mode.aria")}
      className={cn(
        "flex items-stretch",
        compact
          ? "gap-1.5"
          : "gap-2 px-4 py-2",
        "font-mono text-[10px] uppercase tracking-[0.12em]",
      )}
    >
      <ModeButton
        active={mode === "select"}
        onClick={() => onChange("select")}
        testid="mode-select-btn"
        label={t("grid.mode.select")}
        hint={t("grid.mode.selectHint")}
        icon={<CrosshairIcon />}
        compact={compact}
      />
      <ModeButton
        active={mode === "browse"}
        onClick={() => onChange("browse")}
        testid="mode-inspect-btn"
        label={t("grid.mode.inspect")}
        hint={t("grid.mode.inspectHint")}
        icon={<HandIcon />}
        compact={compact}
      />
    </div>
  );
}

interface ModeButtonProps {
  active: boolean;
  onClick: () => void;
  testid: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
  compact?: boolean;
}

function ModeButton({
  active,
  onClick,
  testid,
  label,
  hint,
  icon,
  compact = false,
}: ModeButtonProps) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      role="radio"
      aria-checked={active}
      aria-label={t("grid.mode.itemAria", { label, hint })}
      onClick={onClick}
      className={cn(
        "relative flex items-center gap-2",
        compact ? "px-2 py-1" : "flex-1 px-2.5 py-1.5",
        "transition-colors duration-150",
        "focus:outline-none focus-visible:ring-1",
        "focus-visible:ring-[color:var(--spec-cyan)]",
      )}
      style={{
        clipPath:
          "polygon(6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px), 0 6px)",
        background: active
          ? "linear-gradient(180deg, rgba(45, 182, 240,0.18) 0%, rgba(45, 182, 240,0.08) 100%)"
          : "rgba(255,255,255,0.02)",
        border: `1px solid ${
          active ? "rgba(45, 182, 240,0.55)" : "var(--spec-hairline)"
        }`,
        color: active ? "var(--spec-cyan)" : "var(--text-secondary)",
      }}
    >
      <span
        aria-hidden
        className="inline-flex h-3.5 w-3.5 shrink-0"
        style={{ color: "currentColor" }}
      >
        {icon}
      </span>
      {compact ? (
        <span className="text-[10px] tracking-[0.14em]">{label}</span>
      ) : (
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[10px] tracking-[0.14em]">{label}</span>
          <span
            className="text-[9px] tracking-[0.06em] normal-case"
            style={{
              color: active ? "var(--spec-cyan)" : "var(--text-muted)",
              opacity: active ? 0.85 : 0.7,
            }}
          >
            {hint}
          </span>
        </span>
      )}
      {active && !compact && (
        <span
          aria-hidden
          className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full"
          style={{
            background: "var(--spec-cyan)",
            boxShadow: "0 0 6px var(--spec-cyan)",
          }}
        />
      )}
    </button>
  );
}

function CrosshairIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.2">
      <circle cx="7" cy="7" r="3.2" />
      <line x1="7" y1="0.5" x2="7" y2="3" />
      <line x1="7" y1="11" x2="7" y2="13.5" />
      <line x1="0.5" y1="7" x2="3" y2="7" />
      <line x1="11" y1="7" x2="13.5" y2="7" />
    </svg>
  );
}

function HandIcon() {
  // Simplified hand-cursor glyph — pointer palm + extended index.
  return (
    <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" strokeLinecap="round">
      <path d="M5.5 8.5 V3.4 A0.9 0.9 0 0 1 7.3 3.4 V7.5" />
      <path d="M7.3 7 V2.6 A0.9 0.9 0 0 1 9.1 2.6 V7.6" />
      <path d="M9.1 7.2 V3.8 A0.9 0.9 0 0 1 10.9 3.8 V8.4" />
      <path d="M10.9 5.5 A0.9 0.9 0 0 1 12.4 6.4 V10.1 A3.4 3.4 0 0 1 9 13.5 H7.8 A3.7 3.7 0 0 1 4.3 10.9 L3 8.4 A0.85 0.85 0 0 1 4.4 7.6 L5.5 9" />
    </svg>
  );
}
